import React from 'react';
import { css as compiledCss } from '@compiled/react';

import { cssMap, cx } from '@atlaskit/css';
import AiAgentIcon from '@atlaskit/icon/core/ai-agent';
import IconTile from '@atlaskit/icon/icon-tile';
import { Box } from '@atlaskit/primitives/compiled/box';
import { AgentAvatar } from '@atlaskit/rovo-agent-components';
import { getAgentAvatarColor } from '@atlaskit/rovo-agent-components/ui/agent-avatar/GeneratedAvatar';
import { token } from '@atlaskit/tokens';

import { isForgeAgent } from '@atlassian/conversation-assistant-agent';

import { fg } from '@confluence/feature-gating';

import type { AgentWithPresence } from './AgentPresenceTypes.ts';

const AGENT_AVATAR_CLIP_PATH = `polygon(45% 1.33975%, 46.5798% 0.60307%, 48.26352% 0.15192%, 50% 0%, 51.73648% 0.15192%, 53.4202% 0.60307%, 55% 1.33975%, 89.64102% 21.33975%, 91.06889% 22.33956%, 92.30146% 23.57212%, 93.30127% 25%, 94.03794% 26.5798%, 94.48909% 28.26352%, 94.64102% 30%, 94.64102% 70%, 94.48909% 71.73648%, 94.03794% 73.4202%, 93.30127% 75%, 92.30146% 76.42788%, 91.06889% 77.66044%, 89.64102% 78.66025%, 55% 98.66025%, 53.4202% 99.39693%, 51.73648% 99.84808%, 50% 100%, 48.26352% 99.84808%, 46.5798% 99.39693%, 45% 98.66025%, 10.35898% 78.66025%, 8.93111% 77.66044%, 7.69854% 76.42788%, 6.69873% 75%, 5.96206% 73.4202%, 5.51091% 71.73648%, 5.35898% 70%, 5.35898% 30%, 5.51091% 28.26352%, 5.96206% 26.5798%, 6.69873% 25%, 7.69854% 23.57212%, 8.93111% 22.33956%, 10.35898% 21.33975%)`;
type AgentAvatarSize = 'small' | 'medium';

const AGENT_AVATAR_SHELL_BORDER_WIDTH = 1;
const AGENT_AVATAR_SHELL_SIZE: Record<AgentAvatarSize, { width: number; height: number }> = {
	small: { width: 24, height: 27.75 },
	medium: { width: 32, height: 32 },
};
// Agent avatar shells use six circular corner arcs. The small shell follows the design's
// 24 × 27.75 canvas with a 4px arc radius; the medium shell retains the ADS 10% radius.
const AGENT_AVATAR_SHELL_PATH: Record<AgentAvatarSize, string> = {
	small:
		'M 9.998 1.1574 A 4 4 0 0 1 14.002 1.1574 L 22.002 5.7824 A 4 4 0 0 1 24 9.2454 L 24 18.5046 A 4 4 0 0 1 22.002 21.9676 L 14.002 26.5926 A 4 4 0 0 1 9.998 26.5926 L 1.998 21.9676 A 4 4 0 0 1 0 18.5046 L 0 9.2454 A 4 4 0 0 1 1.998 5.7824 Z',
	medium:
		'M 14.4579 1.5148 A 3.2 3.2 0 0 1 17.5421 1.5148 L 27.6755 7.0882 A 3.2 3.2 0 0 1 29.3333 9.8921 L 29.3333 22.1079 A 3.2 3.2 0 0 1 27.6755 24.9118 L 17.5421 30.4852 A 3.2 3.2 0 0 1 14.4579 30.4852 L 4.3245 24.9118 A 3.2 3.2 0 0 1 2.6667 22.1079 L 2.6667 9.8921 A 3.2 3.2 0 0 1 4.3245 7.0882 Z',
};
const EXTERNAL_AGENT_AVATAR_COLOR = '#DDDEE1';

const externalAgentAvatarBackgroundStyles = compiledCss({
	display: 'inline-flex',
	// Match the hexagonal shape used by AgentAvatar in Studio.
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values
	clipPath: AGENT_AVATAR_CLIP_PATH,
	// Match the neutral background used by the unidentified external-agent avatar in Analytics.
	// eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/design-system/no-unsafe-design-token-usage
	'--ds-background-neutral': EXTERNAL_AGENT_AVATAR_COLOR,
});

const styles = cssMap({
	root: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		flexShrink: 0,
		position: 'relative',
	},
	small: {
		height: '27.75px',
		width: '24px',
	},
	medium: {
		height: '32px',
		width: '32px',
	},
	overlapped: {
		marginInlineStart: token('space.negative.075'),
	},
	avatarContent: {
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		position: 'relative',
	},
	shell: {
		display: 'block',
		position: 'absolute',
		left: '50%',
		pointerEvents: 'none',
	},
	legacyBorder: {
		position: 'relative',
		filter: `drop-shadow(1px 0px 0px ${token('color.icon.inverse')}) drop-shadow(-1px 0px 0px ${token('color.icon.inverse')}) drop-shadow(0px 1px 0px ${token('color.icon.inverse')}) drop-shadow(0px -1px 0px ${token('color.icon.inverse')})`,
	},
});

const ExternalAgentAvatar = ({ size }: { size: AgentAvatarSize }): React.JSX.Element => (
	<span data-testid="external-agent-avatar" css={externalAgentAvatarBackgroundStyles}>
		<IconTile icon={AiAgentIcon} label="" appearance="gray" size={size} />
	</span>
);

const AgentAvatarShell = ({
	backgroundColor,
	borderColor,
	size,
}: {
	backgroundColor: string;
	borderColor: string;
	size: AgentAvatarSize;
}): React.JSX.Element => {
	const { width, height } = AGENT_AVATAR_SHELL_SIZE[size];

	return (
		<Box
			as="span"
			xcss={styles.shell}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Exposes the exact fractional shell offset, rotation, and opacity required by the design specification.
			style={{ top: '0.2px', transform: 'translateX(-50%) rotate(0deg)', opacity: 1 }}
			testId="agent-presence-avatar-shell"
		>
			<svg
				data-testid="agent-presence-avatar-shell-svg"
				display="block"
				width={width}
				height={height}
				viewBox={`0 0 ${width} ${height}`}
				fill="none"
				xmlns="http://www.w3.org/2000/svg"
				aria-hidden="true"
				focusable="false"
			>
				<path
					data-testid="agent-presence-avatar-shell-shape"
					d={AGENT_AVATAR_SHELL_PATH[size]}
					fill={backgroundColor}
					stroke={borderColor}
					strokeWidth={AGENT_AVATAR_SHELL_BORDER_WIDTH}
					vectorEffect="non-scaling-stroke"
				/>
			</svg>
		</Box>
	);
};

const getShellColors = (
	agent: AgentWithPresence,
): { backgroundColor: string; borderColor: string } => {
	switch (true) {
		case agent.isExternalAgentFallback:
			return {
				backgroundColor: EXTERNAL_AGENT_AVATAR_COLOR,
				borderColor: EXTERNAL_AGENT_AVATAR_COLOR,
			};
		default: {
			const { primary } = getAgentAvatarColor({
				agentIdentityAccountId: agent.identity_account_id,
				agentId: agent.id,
				agentNamedId: agent.external_config_reference ?? agent.named_id,
			});

			return { backgroundColor: primary, borderColor: primary };
		}
	}
};

export type AgentPresenceAvatarProps = {
	agent: AgentWithPresence;
	isOverlapped?: boolean;
	stackIndex?: number;
	showBorder?: boolean;
	size?: AgentAvatarSize;
};

export function AgentPresenceAvatar({
	agent,
	isOverlapped,
	stackIndex,
	showBorder,
	size = 'medium',
}: AgentPresenceAvatarProps): React.JSX.Element {
	const renderAgentAvatar = (avatarSize: AgentAvatarSize) =>
		agent.agentAvatarAsset ? (
			<img src={agent.agentAvatarAsset} alt="" width={avatarSize === 'small' ? 24 : 32} />
		) : agent.isExternalAgentFallback ? (
			<ExternalAgentAvatar size={avatarSize} />
		) : (
			<AgentAvatar
				key={agent.id}
				imageUrl={agent.avatarUrl}
				agentId={agent.id}
				agentIdentityAccountId={agent.identity_account_id}
				agentNamedId={agent.external_config_reference ?? agent.named_id}
				size={avatarSize}
				showBorder={false}
				isForgeAgent={isForgeAgent(agent)}
				forgeAgentIconUrl={agent.icon}
			/>
		);

	if (!fg('platform_move_presence_agents')) {
		return (
			<Box
				xcss={cx(showBorder && styles.legacyBorder)}
				// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Restores the legacy runtime z-index used by the standalone agent avatar group.
				style={{ zIndex: stackIndex }}
			>
				{renderAgentAvatar('medium')}
			</Box>
		);
	}

	const avatarSize = showBorder && fg('platform_move_presence_agents') ? 'small' : size;
	const agentAvatar = renderAgentAvatar(avatarSize);
	const shellColors = showBorder && !agent.agentAvatarAsset ? getShellColors(agent) : undefined;

	return (
		<Box
			xcss={cx(
				showBorder && styles.root,
				showBorder && styles[avatarSize],
				isOverlapped && styles.overlapped,
			)}
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Preserves the runtime stacking order used by the facepile.
			style={{ zIndex: stackIndex }}
			testId="agent-presence-avatar"
		>
			{showBorder && shellColors ? (
				<>
					<AgentAvatarShell {...shellColors} size={avatarSize} />
					<Box as="span" xcss={styles.avatarContent}>
						{agentAvatar}
					</Box>
				</>
			) : (
				agentAvatar
			)}
		</Box>
	);
}
